/**
 * Ce que les jauges de la bibliothèque partagent : leur vocabulaire de
 * tonalités, et la seule règle de calcul qu'elles appliquent.
 *
 * ⚠️ **Ce fichier existe à cause de `react-refresh`**, qui interdit à un module
 * exportant des composants d'exporter autre chose — la contrainte même qui
 * avait créé `components/ui/tokens.ts` à l'étape 5. Ce n'est pas une couche
 * d'abstraction : c'est l'endroit où poser ce que `ProgressBar` et
 * `ProgressRing` doivent partager sans se dupliquer.
 */

/**
 * Les tonalités du système.
 *
 * ⚠️ Les calories n'ont pas de teinte propre : elles portent `brand`. Et les
 * trois teintes de macro sont **fixes** — ne jamais les réassigner par écran
 * (`design-tokens.md`, principe 2).
 */
export type BarTone = 'brand' | 'protein' | 'carbs' | 'fat' | 'warn';

/**
 * Part parcourue, bornée à [0, 1].
 *
 * 🔴 **Un objectif nul rend zéro, jamais un.** `consommé / 0` vaut `Infinity`,
 * que la borne haute ramènerait à 1 : une jauge pleine annonçant un objectif
 * atteint qui n'existe pas. Un repas ajouté à la main est exactement ce cas —
 * ses cibles valent zéro depuis l'étape 5.
 *
 * 🔴 **Et la borne haute est le seul endroit où la saturation est décidée.**
 * Une jauge qui ferait deux fois le tour ne se lirait plus ; le restant, lui,
 * n'est jamais borné — c'est ce qu'on vient chercher, dépassement compris
 * (specs §6.2).
 */
export function ratioOf(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.min(1, Math.max(0, value / max));
}
